import {
    inputActionIds,
    inputActions,
    reservedInputs
} from 'app/modules/wallforming/components/input-binding/registry/inputActionsRegistry';
import {
    DEFAULT_INPUT_PROFILE_ID,
    builtInInputProfiles,
    inputPresets,
    isInputActionId,
    isInputPresetId
} from 'app/modules/wallforming/components/input-binding/registry/inputPresetsRegistry';
import { InputDevice } from 'app/modules/wallforming/components/input-binding/types/inputDevice';
import { InputModifier } from 'app/modules/wallforming/components/input-binding/types/inputModifier';
import { InputTrigger } from 'app/modules/wallforming/components/input-binding/types/inputTrigger';
import {
    getChordDevice,
    getMouseButton,
    parseChord
} from 'app/modules/wallforming/components/input-binding/utils/inputChordUtils';
import {
    contextsOverlap,
    scopesOverlap
} from 'app/modules/wallforming/components/input-binding/utils/inputContextUtils';

import type { InputActionId } from 'app/modules/wallforming/components/input-binding/types/inputActionId';
import type { InputBindingIssue } from 'app/modules/wallforming/components/input-binding/types/inputBindingIssue';
import type { InputBindingSlots } from 'app/modules/wallforming/components/input-binding/types/inputBindingSlots';
import type { InputChord } from 'app/modules/wallforming/components/input-binding/types/inputChord';
import type { InputKeymap } from 'app/modules/wallforming/components/input-binding/types/inputKeymap';
import type { InputProfile } from 'app/modules/wallforming/components/input-binding/types/inputProfile';
import type { ResolvedInputBindings } from 'app/modules/wallforming/components/input-binding/types/resolvedInputBindings';
const slotIndexes = (actionId: InputActionId) => Array.from({ length: inputActions[actionId].slots }, (_, i) => i);

export const getPresetChord = (profile: InputProfile, actionId: InputActionId, slot: number): InputChord | null =>
    inputPresets[profile.basedOn][actionId]?.[slot] ?? null;

export const getPresetSlots = (profile: InputProfile, actionId: InputActionId): InputBindingSlots =>
    slotIndexes(actionId).map((slot) => getPresetChord(profile, actionId, slot));

export const getProfileSlots = (profile: InputProfile, actionId: InputActionId): InputBindingSlots =>
    profile.overrides[actionId]
        ? slotIndexes(actionId).map((slot) => profile.overrides[actionId]?.[slot] ?? null)
        : getPresetSlots(profile, actionId);

export function resolveInputBindings(profile: InputProfile): ResolvedInputBindings {
    const slots = {} as Record<InputActionId, InputBindingSlots>;
    const index = new Map<InputChord, InputActionId[]>();
    for (const actionId of inputActionIds) {
        slots[actionId] = getProfileSlots(profile, actionId);
        for (const chord of slots[actionId]) {
            if (chord) {
                index.set(chord, [...(index.get(chord) ?? []), actionId]);
            }
        }
    }
    return { slots, index };
}

/** Returns the profile with one slot replaced, storing the action only while it differs from the preset. */
export function withProfileSlot(
    profile: InputProfile,
    actionId: InputActionId,
    slot: number,
    chord: InputChord | null
): InputProfile {
    const next = getProfileSlots(profile, actionId).map((current, index) => (index === slot ? chord : current));
    const preset = getPresetSlots(profile, actionId);
    const overrides: InputKeymap = { ...profile.overrides, [actionId]: next };
    if (next.every((value, index) => value === preset[index])) {
        delete overrides[actionId];
    }
    return { ...profile, overrides };
}

export function isChordAllowed(actionId: InputActionId, chord: InputChord): boolean {
    const parsed = parseChord(chord);
    if (!parsed) {
        return false;
    }
    if (inputActions[actionId].keyboardModifierOnly) {
        return (
            parsed.modifiers.length === 0 &&
            parsed.trigger === undefined &&
            Object.values(InputModifier).some((modifier) => modifier === parsed.code)
        );
    }
    const { devices, triggers, mouseButtons } = inputActions[actionId];
    const device = getChordDevice(parsed.code);
    return (
        devices.includes(device) &&
        (device !== InputDevice.Mouse ||
            (triggers.includes(parsed.trigger!) &&
                (!mouseButtons || mouseButtons.includes(getMouseButton(parsed.code)!))))
    );
}

export function bindingsCollide(a: InputActionId, b: InputActionId): boolean {
    return (
        contextsOverlap(inputActions[a].contexts, inputActions[b].contexts) &&
        scopesOverlap(inputActions[a].scope, inputActions[b].scope)
    );
}

export function findBindingIssue(
    bindings: ResolvedInputBindings,
    actionId: InputActionId,
    chord: InputChord,
    slot?: number
): InputBindingIssue | null {
    if (!isChordAllowed(actionId, chord)) {
        return { kind: 'notAllowed' };
    }
    if (bindings.slots[actionId].some((current, index) => index !== slot && current === chord)) {
        return { kind: 'sameAction' };
    }
    const reserved = reservedInputs.find(
        (input) => input.chords.includes(chord) && contextsOverlap(input.contexts, inputActions[actionId].contexts)
    );
    if (reserved) {
        return { kind: 'reserved', reserved };
    }
    const actionIds = (bindings.index.get(chord) ?? []).filter(
        (other) => other !== actionId && bindingsCollide(actionId, other)
    );
    return actionIds.length > 0 ? { kind: 'conflict', actionIds } : null;
}

/** Unassigns `chord` from every given action. */
export function withoutChord(profile: InputProfile, actionIds: readonly InputActionId[], chord: InputChord) {
    return actionIds.reduce(
        (current, actionId) =>
            getProfileSlots(current, actionId).reduce<InputProfile>(
                (next, value, slot) => (value === chord ? withProfileSlot(next, actionId, slot, null) : next),
                current
            ),
        profile
    );
}

type StoredProfile = [id: string, name: string, basedOn: string, overrides: InputKeymap];

export interface StoredInputBindings {
    readonly v: 2;
    readonly a: string;
    readonly p: readonly StoredProfile[];
}

export function serializeInputBindings(profiles: readonly InputProfile[], activeId: string): StoredInputBindings {
    return {
        v: 2,
        a: activeId,
        p: profiles
            .filter((profile) => !profile.builtIn || profile.id === DEFAULT_INPUT_PROFILE_ID)
            .map((profile) => [profile.id, profile.name, profile.basedOn, profile.overrides])
    };
}

function sanitizeOverrides(raw: unknown): InputKeymap {
    if (!raw || typeof raw !== 'object') {
        return {};
    }
    return Object.fromEntries(
        Object.entries(raw).flatMap(([actionId, slots]) =>
            isInputActionId(actionId) && Array.isArray(slots)
                ? [
                      [
                          actionId,
                          slotIndexes(actionId).map((slot) => {
                              const chord: unknown = slots[slot];
                              if (typeof chord !== 'string') {
                                  return null;
                              }
                              if (isChordAllowed(actionId, chord)) {
                                  return chord;
                              }
                              if (inputActions[actionId].keyboardModifierOnly) {
                                  const parsed = parseChord(chord);
                                  const modifier = parsed?.modifiers[0];
                                  if (
                                      parsed &&
                                      parsed.trigger === InputTrigger.Drag &&
                                      parsed.code.startsWith('Mouse') &&
                                      parsed.modifiers.length === 1 &&
                                      modifier &&
                                      Object.values(InputModifier).includes(modifier)
                                  ) {
                                      return modifier;
                                  }
                              }
                              return null;
                          })
                      ]
                  ]
                : []
        )
    );
}

/** Rebuilds profiles from untrusted data (cookie/import); invalid parts fall back to the built-in defaults. */
export function deserializeInputBindings(data: unknown): { profiles: InputProfile[]; activeId: string } {
    const stored = data as Partial<StoredInputBindings> | null;
    const entries = stored?.v === 2 && Array.isArray(stored.p) ? stored.p : [];
    const restored = new Map<string, InputProfile>();
    for (const entry of entries) {
        const [id, name, basedOn, overrides] = Array.isArray(entry) ? entry : [];
        if (typeof id === 'string' && typeof name === 'string' && isInputPresetId(basedOn)) {
            restored.set(id, { id, name, basedOn, overrides: sanitizeOverrides(overrides) });
        }
    }

    const builtIns = builtInInputProfiles.map((profile) => {
        const saved = restored.get(profile.id);
        return {
            ...profile,
            name: profile.id === DEFAULT_INPUT_PROFILE_ID ? (saved?.name ?? profile.name) : profile.name,
            overrides: profile.readOnly ? {} : (saved?.overrides ?? {})
        };
    });
    const custom = [...restored.values()].filter((profile) => !builtIns.some((builtIn) => builtIn.id === profile.id));
    const profiles = [...builtIns, ...custom];
    const activeId =
        typeof stored?.a === 'string' && profiles.some((profile) => profile.id === stored.a)
            ? stored.a
            : DEFAULT_INPUT_PROFILE_ID;
    return { profiles, activeId };
}

if (import.meta.env.DEV) {
    for (const profile of builtInInputProfiles) {
        const bindings = resolveInputBindings(profile);
        for (const actionId of inputActionIds) {
            bindings.slots[actionId].forEach((chord, slot) => {
                const issue = chord && findBindingIssue(bindings, actionId, chord, slot);
                if (issue) {
                    // eslint-disable-next-line no-console
                    console.error(`[input-binding] preset "${profile.id}": ${actionId} -> ${chord}`, issue);
                }
            });
        }
    }
}
